import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldAuth } from '../../contexts/FieldAuthContext';
import { mobileApi } from '../../services/api';
import './FieldDashboard.css';

interface WorkplaceItem {
  id: number;
  name: string;
  dangerClass: string;
  employeeCount: number;
  address: string;
  phone?: string | null;
  contactPerson?: string | null;
  sgkSicilNo?: string | null;
  city?: string;
  district?: string;
  isCompletedThisMonth: boolean;
  lastVisitDate?: string | null;
  lastVisitNotes?: string | null;
  progress?: number;
}

interface DashboardStats {
  totalWorkplaceCount: number;
  totalEmployeeCount: number;
  completedVisitCount: number;
  remainingVisitCount: number;
  monthlyProgress: number;
  minutesRemaining: number;
}

// Tehlike Sınıfı Rengi (ISG Destek ile birebir aynı)
const getHazardColor = (dc: string) => {
  const s = (dc || '').toLowerCase().trim();
  if (s.includes('çok') || s.includes('cok') || s.includes('high') || s.includes('very')) {
    return '#F44336'; // Kırmızı
  }
  if (s.includes('az') || s.includes('low') || s.includes('less')) {
    return '#4CAF50'; // Yeşil
  }
  return '#FF9800'; // Turuncu / Tehlikeli
};

const getHazardText = (dc: string) => {
  const s = (dc || '').toLowerCase().trim();
  if (s.includes('çok') || s.includes('cok') || s.includes('high') || s.includes('very')) {
    return 'Çok Tehlikeli';
  }
  if (s.includes('az') || s.includes('low') || s.includes('less')) {
    return 'Az Tehlikeli';
  }
  return 'Tehlikeli';
};

// İlerleme Barı Rengi (ISG Destek exact: Yeşil >= 60, Turuncu >= 20, Kırmızı < 20)
const getProgressColor = (progress: number) => {
  if (progress >= 60) return '#4CAF50';
  if (progress >= 20) return '#FF9800';
  return '#F44336';
};

// SGK Sicil No Formatlayıcı (ISG Destek aralıklı font)
const formatSgkSicil = (sgk?: string | null) => {
  if (!sgk) return '';
  const raw = sgk.replace(/\D/g, '');
  if (!raw) return sgk;
  const groups = [1, 4, 2, 2, 7, 3, 2, 2, 3];
  let idx = 0;
  const parts: string[] = [];
  for (const len of groups) {
    if (idx >= raw.length) break;
    parts.push(raw.substring(idx, idx + len));
    idx += len;
  }
  return parts.filter(p => p.length > 0).join(' ');
};

const FieldDashboard: React.FC = () => {
  const { user, logout } = useFieldAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats>({
    totalWorkplaceCount: 0,
    totalEmployeeCount: 0,
    completedVisitCount: 0,
    remainingVisitCount: 0,
    monthlyProgress: 0,
    minutesRemaining: 0
  });

  const [workplaces, setWorkplaces] = useState<WorkplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'remaining' | 'completed'>('all');
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Titreşimli Geri Bildirim (Haptic Feedback)
  const triggerHaptic = (type: 'light' | 'medium' | 'success' = 'light') => {
    if (typeof window !== 'undefined' && 'navigator' in window && 'vibrate' in navigator) {
      try {
        if (type === 'light') navigator.vibrate(10);
        else if (type === 'medium') navigator.vibrate(25);
        else if (type === 'success') navigator.vibrate([15, 40, 15]);
      } catch (e) {
        // Haptic desteklenmiyorsa sessizce geç
      }
    }
  };

  const fetchDashboardData = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      else setRefreshing(true);

      const res = await mobileApi.getDashboard();
      if (res && res.success) {
        if (res.stats) {
          setStats({
            totalWorkplaceCount: res.stats.totalWorkplaceCount || res.stats.assignedWorkplaceCount || 0,
            totalEmployeeCount: res.stats.totalEmployeeCount || 0,
            completedVisitCount: res.stats.completedVisitCount || 0,
            remainingVisitCount: res.stats.remainingVisitCount || 0,
            monthlyProgress: res.stats.monthlyProgress || 0,
            minutesRemaining: res.stats.minutesRemaining || 0
          });
        }
        if (res.workplaces) setWorkplaces(res.workplaces);
        if (isSilent) triggerHaptic('success');
      }
    } catch (err: any) {
      console.error('Mobil saha verisi yüklenirken hata:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // İlerleme çubuğu animasyonu (ISG Destek style)
  useEffect(() => {
    if (stats.monthlyProgress >= 0) {
      setAnimatedProgress(0);
      const target = stats.monthlyProgress;
      const duration = 1200;
      const interval = 20;
      const step = target / (duration / interval);

      const timer = setInterval(() => {
        setAnimatedProgress(prev => {
          const next = prev + step;
          if (next >= target) {
            clearInterval(timer);
            return target;
          }
          return next;
        });
      }, interval);

      return () => clearInterval(timer);
    }
  }, [stats.monthlyProgress]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Türkçe Karakter Uyumlu Arama ve Filtreleme
  const filteredWorkplaces = useMemo(() => {
    let result = [...workplaces];

    if (filter === 'remaining') {
      result = result.filter(wp => !wp.isCompletedThisMonth);
    } else if (filter === 'completed') {
      result = result.filter(wp => wp.isCompletedThisMonth);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLocaleLowerCase('tr-TR');
      result = result.filter(wp => {
        const nameMatch = (wp.name || '').toLocaleLowerCase('tr-TR').includes(q);
        const addressMatch = (wp.address || '').toLocaleLowerCase('tr-TR').includes(q);
        const sgkMatch = (wp.sgkSicilNo || '').toLocaleLowerCase('tr-TR').includes(q);
        const contactMatch = (wp.contactPerson || '').toLocaleLowerCase('tr-TR').includes(q);
        return nameMatch || addressMatch || sgkMatch || contactMatch;
      });
    }

    return result;
  }, [workplaces, filter, searchQuery]);

  if (loading) {
    return (
      <div className="field-dashboard">
        {/* İskelet Header */}
        <div className="field-skeleton-header">
          <div className="field-skeleton-line short" />
          <div className="field-skeleton-line title" />
          <div className="field-skeleton-line badge" />
        </div>

        {/* İskelet İlerleme */}
        <div className="field-skeleton-card" style={{ height: '70px' }}>
          <div className="field-skeleton-line full" />
        </div>

        {/* İskelet Grid */}
        <div className="field-stats-grid-2x2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="field-skeleton-card" style={{ height: '90px' }} />
          ))}
        </div>

        {/* İskelet İşyeri Kartları */}
        <div className="field-workplaces-list">
          {[1, 2, 3].map(i => (
            <div key={i} className="field-skeleton-card" style={{ height: '140px' }}>
              <div className="field-skeleton-line title" style={{ marginBottom: '12px' }} />
              <div className="field-skeleton-line short" />
              <div className="field-skeleton-line full" style={{ marginTop: '8px' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const displayRole = (user as any)?.expertiseClass || user?.role || 'A Sınıfı İSG Uzmanı';

  return (
    <div className="field-dashboard">
      {/* 1. Header (ISG Destek 1:1 Sticky Gradient Header) */}
      <div className="field-dashboard-header">
        <div className="header-info-area">
          <h1 className="personnel-name">{user?.fullName || 'İSG Uzmanı'}</h1>
          <p className="expertise-class">🛡️ {displayRole}</p>
        </div>

        <div className="header-nav-group">
          <button
            type="button"
            className="header-nav-btn active"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <span className="nav-icon">🏠</span>
            <span className="nav-text">Ana Sayfa</span>
          </button>

          <button
            type="button"
            className="header-nav-btn"
            onClick={() => navigate('/field/planning')}
          >
            <span className="nav-icon">📅</span>
            <span className="nav-text">Planlama</span>
          </button>

          <button
            type="button"
            className="header-nav-btn"
            onClick={() => {
              if (window.confirm('Mobil saha oturumunuzu kapatmak istiyor musunuz?')) {
                logout();
                navigate('/field/login', { replace: true });
              }
            }}
          >
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Çıkış</span>
          </button>
        </div>
      </div>

      {refreshing && (
        <div className="field-pull-indicator" style={{ height: '36px', opacity: 1 }}>
          <span className="field-pull-icon spinning">
            ⏳ Veriler güncelleniyor...
          </span>
        </div>
      )}

      {/* 2. Aylık İlerleme Kartı (ISG Destek Exact Design) */}
      <div className="field-progress-section-card">
        <div className="field-header-row">
          <h2>
            Aylık İlerleme
            <span className="header-subtitle">(Ortalama Yüzdeniz)</span>
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
          <div className="progress-bar-bg-slim">
            <div
              className="progress-bar-fill"
              style={{
                width: `${animatedProgress}%`,
                backgroundColor: getProgressColor(animatedProgress)
              }}
            />
          </div>
          <span className="progress-value-text">
            %{Math.round(animatedProgress)}
          </span>
        </div>
      </div>

      {/* 3. Arama Kutusu (ISG Destek Smart Search) */}
      <div className="field-search-section">
        <div className="field-search-header">
          <span style={{ fontSize: '18px' }}>🔎</span>
          <h3>İşyeri Ara</h3>
        </div>
        <div className="field-search-input-wrapper">
          <input
            type="text"
            className="field-search-input-isg"
            placeholder="🔍 İşyeri adı, adres veya SGK No yazın..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="field-search-clear-btn"
              onClick={() => setSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 4. 2x2 Stats Grid (ISG Destek Exact Square Boxes) */}
      <div className="field-stats-grid-2x2">
        {/* 1. Atanan / Kayıtlı İşyeri */}
        <div
          className={`stat-box-card ${filter === 'all' ? 'active-tab' : ''}`}
          onClick={() => {
            triggerHaptic('light');
            setFilter('all');
          }}
        >
          <div className="stat-box-icon">🏭</div>
          <div className="stat-box-value" style={{ color: '#4CAF50' }}>
            {stats.totalWorkplaceCount}
          </div>
          <div className="stat-box-label">Kayıtlı İşyeri</div>
        </div>

        {/* 2. Toplam Çalışan */}
        <div
          className="stat-box-card"
          onClick={() => {
            triggerHaptic('light');
            setFilter('all');
          }}
        >
          <div className="stat-box-icon">👥</div>
          <div className="stat-box-value" style={{ color: '#2196F3' }}>
            {stats.totalEmployeeCount}
          </div>
          <div className="stat-box-label">Toplam Çalışan</div>
        </div>

        {/* 3. Kalan Ziyaret */}
        <div
          className={`stat-box-card ${filter === 'remaining' ? 'active-tab' : ''}`}
          onClick={() => {
            triggerHaptic('light');
            setFilter('remaining');
          }}
        >
          <div className="stat-box-icon">🏃</div>
          <div className="stat-box-value" style={{ color: '#FF9800' }}>
            {stats.remainingVisitCount}
          </div>
          <div className="stat-box-label">Kalan Ziyaret</div>
        </div>

        {/* 4. Kalan Dakika */}
        <div
          className="stat-box-card"
          onClick={() => {
            triggerHaptic('light');
            setFilter('completed');
          }}
        >
          <div className="stat-box-icon">⏳</div>
          <div className="stat-box-value" style={{ color: stats.minutesRemaining < 0 ? '#F44336' : '#9C27B0' }}>
            {stats.minutesRemaining || 0}
          </div>
          <div className="stat-box-label">Kalan Dakika</div>
        </div>
      </div>

      {/* 5. İşyerleri Listesi (ISG Destek Exact Card Structure) */}
      <div className="field-workplaces-section" id="workplaces-list">
        <h2>
          {searchQuery.trim().length > 0
            ? 'Arama Sonuçları'
            : filter === 'remaining'
            ? 'Ziyaret Bekleyenler'
            : filter === 'completed'
            ? 'Tamamlanan Ziyaretler'
            : 'Tüm İşyerleri'}
          {' '}({filteredWorkplaces.length})
        </h2>

        {filteredWorkplaces.length === 0 ? (
          <div className="field-empty-state">
            <span className="empty-icon">📭</span>
            <p>
              {searchQuery
                ? `"${searchQuery}" aramasıyla eşleşen işyeri bulunamadı.`
                : filter === 'remaining'
                ? 'Tebrikler! Bu ay yapılması gereken tüm ziyaretler tamamlandı. 🎉'
                : 'Henüz kayıtlı işyeri bulunmuyor.'}
            </p>
          </div>
        ) : (
          <div className="field-workplaces-list">
            {filteredWorkplaces.map((wp) => {
              const hazardColor = getHazardColor(wp.dangerClass);
              const hazardText = getHazardText(wp.dangerClass);
              const isVisited = wp.isCompletedThisMonth;
              const formattedSgk = formatSgkSicil(wp.sgkSicilNo);
              const wpProgress = wp.progress ?? 0;

              return (
                <div
                  key={wp.id}
                  className={`field-workplace-card ${isVisited ? 'status-visited' : 'status-pending'}`}
                  onClick={() => navigate(`/field/workplace/${wp.id}`)}
                >
                  {/* Başlık Satırı & Telefon / WhatsApp */}
                  <div className="workplace-header">
                    <h3 className="workplace-name">{wp.name}</h3>
                    {wp.phone && (
                      <div className="workplace-header-actions" onClick={(e) => e.stopPropagation()}>
                        <a
                          href={`tel:${wp.phone}`}
                          className="field-call-btn"
                          title={`Ara: ${wp.phone}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                          </svg>
                        </a>
                        <a
                          href={`https://wa.me/90${wp.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="field-wa-btn"
                          title={`WhatsApp: ${wp.phone}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Adres & Harita */}
                  <div className="workplace-details">
                    <div className="workplace-info">
                      <span className="info-icon">📍</span>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(wp.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="maps-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {wp.address}
                      </a>
                    </div>

                    {/* Çalışan Sayısı & Tehlike Sınıfı */}
                    <div className="workplace-meta-row">
                      <span className="info-text">
                        <span className="info-icon">👥</span>
                        {wp.employeeCount} çalışan
                      </span>

                      <span className="risk-badge" style={{ color: hazardColor }}>
                        ⚠️ {hazardText}
                      </span>
                    </div>

                    {/* SGK Sicil No */}
                    {formattedSgk && (
                      <div className="sgk-sicil-text">
                        SGK: {formattedSgk}
                      </div>
                    )}
                  </div>

                  {/* İlerleme Barı (ISG Destek exact) */}
                  <div className="workplace-progress">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${Math.min(100, Math.max(0, wpProgress))}%`,
                          backgroundColor: getProgressColor(wpProgress)
                        }}
                      />
                    </div>
                    <span className="progress-text">%{Math.round(wpProgress)}</span>
                  </div>

                  {/* Kart Aksiyon Satırı: Durum & Ok */}
                  <div className="workplace-action">
                    <span className={`approval-badge ${isVisited ? 'visited' : 'pending'}`}>
                      {isVisited ? '✅ Ziyaret Edildi' : '🏃 Bekliyor'}
                    </span>
                    <span className="arrow">›</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldDashboard;
